/**
 * Générateur — Il construit et met en relation différentes représentations
 * des solides étudiés au cours du cycle.
 * Attendu ID : 239 | Niveau : 3ème | Domaine : Espace (d20, O2)
 *
 * Source doc prof (ESPACE ET GEOMETRIE 3e, séances D6S1-D6S2). Plan
 * auto-validé 2026-07-18 (TODO §6) : « patrons » (patron décrit → solide,
 * relier), « pave » (V/F dans le pavé ABCDEFGH — table de vérités standard,
 * dont LE piège « la perspective déforme »), « sections » (nature de la
 * section par un plan // ou ⊥ à la base, QCM), « sectionsphere » (rayon de la
 * section par Pythagore, triplets exacts), « vues » (vue de dessus, QCM).
 * Adaptation : les constructions (perspectives, tracés) sont portées par la
 * reconnaissance — pas d'interactif de dessin.
 */

import { randomChoice, randInt, shuffle, tex, makeQcm, makeTexteTrou, makeRelier, makeVraiFaux } from '../../../utils';

const ATTENDU_ID = 239;
const L_PATRONS = 'Reconnaître un solide d\'après son patron';
const L_PAVE = 'Vrai ou faux dans le pavé droit';
const L_SECTIONS = 'Nature d\'une section plane';
const L_SECTIONSPHERE = 'Le rayon d\'une section de sphère (Pythagore)';
const L_VUES = 'La vue de dessus d\'un solide';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « patrons » — relier ═════════

const PATRONS = [
    ['6 carrés identiques disposés en croix', 'un cube'],
    ['6 rectangles (3 paires identiques)', 'un pavé droit'],
    ['2 disques et 1 rectangle', 'un cylindre'],
    ['1 carré et 4 triangles isocèles identiques', 'une pyramide à base carrée'],
    ['2 triangles identiques et 3 rectangles', 'un prisme droit à base triangulaire'],
    ['1 disque et 1 secteur de disque', 'un cône'],
    ['2 hexagones réguliers identiques et 6 rectangles', 'un prisme droit à base hexagonale'],
    ['4 triangles équilatéraux identiques', 'un tétraèdre régulier'],
];

const genPatronsRelier = () => {
    const tirage = shuffle(PATRONS).slice(0, 4);
    const payload = makeRelier(tirage.map(([p, s]) => ({ source: p, target: s })),
        '<p>Chaque description est celle d\'un PATRON, c\'est-à-dire du solide mis à plat.</p>'
        + '<p><b>Associe chaque patron au solide qu\'il permet de construire.</b></p>', opts(L_PATRONS));
    payload._meta.params = { tirage };
    return payload;
};

// ═════════ « pave » — V/F, table de vérités standard ═════════
// Convention FIXE : ABCD face du bas, EFGH face du haut (E au-dessus de A…).

const AFFIRMATIONS_PAVE = [
    ['Sur le dessin en perspective, EF et FG semblent égales, donc EF = FG.', false],
    ['BFGC est un rectangle.', true],
    ['Les droites (HB) et (EF) sont sécantes.', false],
    ['Les droites (HB) et (EC) sont sécantes.', true],
    ['FC = BG (diagonales d\'une même face).', true],
    ['La droite (AB) est perpendiculaire à la droite (BF).', true],
    ['Les arêtes cachées se dessinent en pointillés.', true],
    ['Toutes les faces d\'un pavé droit sont des carrés.', false],
];

const genPaveVf = () => {
    let tirage = shuffle(AFFIRMATIONS_PAVE).slice(0, 4);
    if (tirage.every(([, v]) => v) || tirage.every(([, v]) => !v)) {
        const autre = AFFIRMATIONS_PAVE.find(([, v]) => v !== tirage[0][1]);
        tirage = [...tirage.slice(0, 3), autre];
    }
    const payload = makeVraiFaux(tirage.map(a => a[0]), tirage.map(a => (a[1] ? 0 : 1)),
        '<p>ABCDEFGH est un pavé droit dessiné en perspective cavalière : ABCD en bas, EFGH en haut, '
        + 'E au-dessus de A, F au-dessus de B. Ce pavé n\'est PAS un cube.</p>'
        // « la perspective déforme les longueurs ! » désamorçait LE piège de la
        // carte : lire une longueur directement sur le dessin.
        + '<p><b>Vrai ou faux ?</b></p>',
        opts(L_PAVE));
    payload._meta.params = { tirage };
    return payload;
};

// ═════════ « sections » — QCM ═════════

// [solide, plan, section] — plans // à la base ou ⊥ (vertical par l'axe).
const SECTIONS = [
    ['un pavé droit', 'parallèle à sa base', 'un rectangle identique à la base'],
    ['un cylindre', 'parallèle à sa base', 'un cercle identique à la base'],
    ['un cylindre', 'parallèle à son axe', 'un rectangle'],
    ['une pyramide à base carrée', 'parallèle à sa base', 'un carré plus petit que la base (réduction)'],
    ['un cône', 'parallèle à sa base', 'un cercle plus petit que la base (réduction)'],
    ['une sphère', 'quelconque', 'un cercle'],
    ['un cube', 'parallèle à une face', 'un carré identique à la face'],
];

// Distracteurs LOGIQUEMENT VRAIS à bannir selon la bonne réponse (un cercle
// réduit EST « un cercle », un carré EST « un rectangle »…) — sinon un élève
// juste serait compté faux.
const EXCLUS_SECTIONS = {
    'un rectangle identique à la base': ['un rectangle', 'un carré identique à la face'],
    'un cercle identique à la base': ['un cercle'],
    'un carré plus petit que la base (réduction)': ['un rectangle'],
    'un cercle plus petit que la base (réduction)': ['un cercle'],
    'un cercle': ['un cercle identique à la base', 'un cercle plus petit que la base (réduction)'],
    'un carré identique à la face': ['un rectangle', 'un rectangle identique à la base', 'un carré plus petit que la base (réduction)'],
};

const genSectionsQcm = () => {
    const [solide, plan, correct] = randomChoice(SECTIONS);
    const exclus = EXCLUS_SECTIONS[correct] || [];
    const autres = shuffle(SECTIONS.map(s => s[2])
        .filter((v, i, t) => t.indexOf(v) === i)
        .filter(s => s !== correct && !exclus.includes(s)));
    const payload = makeQcm([correct, ...autres.slice(0, 3)], correct,
        `<p>On coupe ${solide} par un plan ${plan}.</p>`
        + '<p><b>Quelle est la nature de la section obtenue ?</b></p>',
        opts(L_SECTIONS));
    payload._meta.params = { solide, plan, correct };
    return payload;
};

// ═════════ « sectionsphere » — Pythagore, triplets exacts ═════════

const TRIPLETS = [[5, 3, 4], [5, 4, 3], [10, 6, 8], [10, 8, 6], [13, 5, 12], [13, 12, 5], [15, 9, 12], [25, 7, 24], [17, 8, 15],
    [15, 12, 9], [20, 12, 16], [20, 16, 12], [26, 10, 24], [26, 24, 10], [30, 18, 24], [34, 16, 30], [25, 15, 20], [25, 20, 15], [40, 24, 32], [50, 14, 48], [17, 15, 8]];

const genSectionSphereTrous = () => {
    const [R, d, r] = randomChoice(TRIPLETS);
    const phrase = `$O'M^{2} = ${R}^{2} - ${d}^{2} = {{${r * r}}} \\qquad`
        + ` \\text{le rayon de la section vaut } {{${r}}}\\ \\text{cm}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une sphère de centre O et de rayon ${R} cm est coupée par un plan horizontal passant par O', `
        + `avec OO' = ${d} cm. Pour un point M de la section, le triangle OO'M est rectangle en O' `
        + `et OM = ${R} cm, le rayon de la sphère. La section est un CERCLE de rayon O'M.</p>`
        // « (Pythagore) » nommait le théorème à mobiliser.
        + `<p><b>Calcule ${tex("O'M^{2}")}, puis le rayon de la section.</b></p>`,
        { ...opts(L_SECTIONSPHERE), mode: 'saisie' });
    payload._meta.params = { R, d, r };
    return payload;
};

// ═════════ « vues » — QCM ═════════

const VUES = [
    ['un cube posé sur une face', 'un carré'],
    ['un cylindre posé sur sa base', 'un cercle'],
    ['un cône posé sur sa base', 'un cercle'],
    ['une pyramide posée sur sa base carrée', 'un carré'],
    ['un pavé droit (non cube) posé sur une face', 'un rectangle'],
    ['une boule', 'un cercle'],
];

const genVuesQcm = () => {
    const [solide, correct] = randomChoice(VUES);
    const matiere = randomChoice(['en bois', 'en verre', 'en métal', 'en carton']);
    const autres = ['un carré', 'un cercle', 'un rectangle', 'un triangle'].filter(v => v !== correct);
    const payload = makeQcm([correct, ...shuffle(autres).slice(0, 3)], correct,
        `<p>On regarde ${solide} — ${matiere} — exactement depuis le dessus.</p>`
        + '<p><b>Quelle est la forme de la vue de dessus ?</b></p>',
        opts(L_VUES));
    payload._meta.params = { solide, correct };
    return payload;
};

const GENERATORS = {
    patrons_relier: genPatronsRelier,
    pave_tableau_vf: genPaveVf,
    sections_qcm_unique: genSectionsQcm,
    sectionsphere_texte_trou_saisie: genSectionSphereTrous,
    vues_qcm_unique: genVuesQcm,
};
const FORMAT_LABELS = { relier: 'Relier', tableau_vf: 'Vrai/Faux', qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'patrons', label: L_PATRONS, formatsList: buildFormats('patrons') },
    { id: 'pave', label: L_PAVE, formatsList: buildFormats('pave') },
    { id: 'sections', label: L_SECTIONS, formatsList: buildFormats('sections') },
    { id: 'sectionsphere', label: L_SECTIONSPHERE, formatsList: buildFormats('sectionsphere') },
    { id: 'vues', label: L_VUES, formatsList: buildFormats('vues') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
